import { Box, Card, CardMedia, Grid, IconButton, Modal, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import images from '../data/gallery.json'; // Import the JSON file

const imageFolderPath = `/images/gallery/`;

function Gallery() {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [shuffledImages, setShuffledImages] = useState([]);

  const handleOpen = (index) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentIndex(null);
  };

  const showNext = () =>
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);

  const showPrev = () =>
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;

      if (event.key === 'ArrowRight') {
        showNext();
      } else if (event.key === 'ArrowLeft') {
        showPrev();
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };
    // Attach keydown listener when the modal is open
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      // Cleanup the event listener
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    // Function to shuffle the images array
    const shuffleArray = (array) => {
      let shuffled = array.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    setShuffledImages(shuffleArray(images));
  }, []);

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ marginBottom: 4 }}>
        Gallery
      </Typography>
      <Grid container spacing={3}> {/* Increased spacing between images */}
        {shuffledImages.map((image, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                transition: 'transform 0.2s ease-in-out',
                '&:hover': { transform: 'scale(1.05)' },
              }}
              onClick={() => handleOpen(index)}
            >
              <CardMedia
                component="img"
                image={`${imageFolderPath}${image}`}
                loading="lazy"
                alt={`Gallery Image ${index + 1}`}
                sx={{
                  width: '100%',
                  height: 'auto', // Maintain aspect ratio
                  cursor: 'pointer',
                  display: 'block', // Ensures the image is visible
                }}
              />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Lightbox Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 'none',
          }}
          onClick={handleClose}
        >
          {currentIndex !== null && (
            <img
              src={`${imageFolderPath}${shuffledImages[currentIndex]}`}
              alt={`Gallery ${currentIndex + 1}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '92vw',
                maxHeight: '88vh',
                objectFit: 'contain', // Never crop — size box to the image
                display: 'block',
                borderRadius: 4,
                boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              }}
            />
          )}
          <IconButton
            aria-label="Close gallery"
            onClick={handleClose}
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            aria-label="Previous image"
            onClick={(e) => { e.stopPropagation(); showPrev(); }}
            sx={{
              position: 'fixed',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
          <IconButton
            aria-label="Next image"
            onClick={(e) => { e.stopPropagation(); showNext(); }}
            sx={{
              position: 'fixed',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
}

export default Gallery;
